import {formatDate, formatNumber} from '@angular/common';
import {Component, ElementRef, EventEmitter, Input, OnChanges, OnDestroy, OnInit, Output, SimpleChanges} from '@angular/core';
import {Subscription} from 'rxjs';
import {map} from 'rxjs/operators';
import {AdminDashboardDataService} from '@features/admin-panel/services/admin-dashboard-data.service';
import {
  AdminDashboardActivityRanking,
  AdminDashboardActivityRow,
  AdminDashboardQueryParams,
  AdminDashboardWidgetState
} from '@features/admin-panel/models/admin-dashboard.models';
import {
  AdminDashboardReportBlock,
  AdminDashboardReportColumn,
  AdminDashboardReportProvider,
  AdminDashboardReportRow
} from '@features/admin-panel/models/admin-dashboard-report.models';
import {AdminDashboardReportRegistry} from '@features/admin-panel/services/admin-dashboard-report-registry.service';
import {AdminDashboardDrillService} from '../drill/admin-dashboard-drill.service';
import {adbDrillRowView, adbPeriodLong} from '@features/admin-panel/helpers/admin-dashboard-drill';
import {adbUsdFull} from '@features/admin-panel/helpers/admin-dashboard-format';
import {
  AdminDashboardDrillCell,
  AdminDashboardDrillView,
  AdminDashboardSilentCompaniesDrill,
  AdminDashboardSilentCompanyDrillRow
} from '@features/admin-panel/models/admin-dashboard-drill.models';

/**
 * Users & adoption "Most and least active companies": clients by logins per seat per week (paying, three seats or
 * more), non-clients by total logins; each list's bars are scaled to its own top value. A company opens the why
 * panel (`companySelected`); "N companies did not log in at all" in the description opens the drill of every one
 * of them (ITP-E-159-10), whose Evidence opens the why panel too. Reloads whenever the dashboard's query params change.
 */
@Component({
  standalone: false,
  selector: 'app-admin-dashboard-activity-ranking',
  templateUrl: './admin-dashboard-activity-ranking.component.html',
  styleUrls: ['./admin-dashboard-activity-ranking.component.scss']
})
export class AdminDashboardActivityRankingComponent implements OnChanges, OnInit, OnDestroy, AdminDashboardReportProvider {
  @Input() params: AdminDashboardQueryParams;
  /** A company row was clicked: the dashboard opens its why panel (OCA-E-159 DetailSections.tsx:124-140). */
  @Output() companySelected = new EventEmitter<string>();
  /** What the card has to show, reported to the dashboard (ITP-E-173-01). */
  @Output() stateChange = new EventEmitter<AdminDashboardWidgetState>();
  widgetState: AdminDashboardWidgetState = 'loading';

  readonly failedTitle = 'Couldn\'t load the most and least active companies';

  ranking: AdminDashboardActivityRanking | null = null;
  loaded = false;
  /** The last load failed: the card says so instead of looking empty. */
  failed = false;

  private loadSubscription: Subscription;

  constructor(private dataService: AdminDashboardDataService, private reportRegistry: AdminDashboardReportRegistry, private host: ElementRef<HTMLElement>, private drill: AdminDashboardDrillService) {}

  ngOnChanges(changes: SimpleChanges): void {
    if (changes['params']) {
      this.load();
    }
  }

  ngOnInit(): void {
    this.reportRegistry.register('users-adoption', this.host.nativeElement, this);
  }

  ngOnDestroy(): void {
    this.loadSubscription?.unsubscribe();
    this.reportRegistry.unregister(this);
  }

  /** The card for the PDF report, built from what it shows now; never reloads (ITP-E-146-09). */
  reportBlocks(): AdminDashboardReportBlock[] {
    if (!this.loaded) {
      return [];
    }
    if (this.failed) {
      return [{kind: 'empty', title: 'Most and least active companies', text: this.failedTitle}];
    }
    const ranking = this.ranking;
    const blocks: AdminDashboardReportBlock[] = [];
    if (ranking?.most?.length) {
      const columns: AdminDashboardReportColumn[] = [
        {header: 'Company', weight: 3},
        {header: this.perSeat ? 'Logins per seat per week' : 'Logins', align: 'right', weight: 2}
      ];
      const toRows = (list: AdminDashboardActivityRow[]): AdminDashboardReportRow[] =>
        list.map((row): AdminDashboardReportRow => ({cells: [row.companyName, this.formatValue(row)]}));
      blocks.push({kind: 'table', title: 'Most active companies', subtitle: this.subtitle, columns, rows: toRows(ranking.most)});
      if (ranking.least?.length) {
        blocks.push({kind: 'table', title: 'Least active companies', subtitle: this.subtitle, columns, rows: toRows(ranking.least)});
      }
    } else {
      const empty: AdminDashboardReportBlock = {kind: 'empty', title: 'Most and least active companies', text: this.emptyText};
      if (ranking) {
        empty.subtitle = this.subtitle;
      }
      blocks.push(empty);
    }
    if (ranking?.silentCount) {
      const silent = ranking.silent ?? [];
      const block: AdminDashboardReportBlock = {
        kind: 'table',
        title: 'Companies that did not log in',
        subtitle: this.silentText,
        columns: [{header: 'Company'}],
        rows: silent.map((row): AdminDashboardReportRow => ({cells: [row.companyName]}))
      };
      if (ranking.silentCount > silent.length) {
        block.note = `+${ranking.silentCount - silent.length} more`;
      }
      blocks.push(block);
    }
    return blocks;
  }

  get perSeat(): boolean {
    return this.ranking?.metric !== 'logins';
  }

  get subtitle(): string {
    return this.perSeat
      ? 'Logins per seat per week in the period, paying companies with three seats or more.'
      : 'Logins in the period, companies not in Client Manager.';
  }

  get emptyText(): string {
    return this.perSeat ? 'No paying companies in this view' : 'No logins in this view';
  }

  get silentText(): string {
    const count = this.ranking?.silentCount ?? 0;
    return `${formatNumber(count, 'en-US', '1.0-0')} ${count === 1 ? 'company' : 'companies'} did not log in at all`;
  }

  /** "1.4" per seat (two decimals below 1), or "1,200" logins. */
  formatValue(row: AdminDashboardActivityRow): string {
    if (!this.perSeat) {
      return formatNumber(row.value, 'en-US', '1.0-0');
    }
    return row.value < 1 ? row.value.toFixed(2) : row.value.toFixed(1);
  }

  /**
   * The row's bar as a share of its OWN list's top value (at least 2% so a bar is always visible): the least active
   * companies are compared with each other, not with the overall leader (OCA-E-159 charts.ts:227-257).
   */
  barWidth(row: AdminDashboardActivityRow, list: AdminDashboardActivityRow[]): number {
    const max = Math.max(0, ...(list ?? []).map(item => item.value));
    return max > 0 ? Math.max(2, 100 * row.value / max) : 0;
  }

  /** A ranked company was clicked. */
  select(row: AdminDashboardActivityRow): void {
    this.companySelected.emit(row.companyId);
  }

  /**
   * "N companies did not log in at all" (F7; OCA-E-159 DetailSections.tsx:131-148): the drill lists every one of them.
   * Clients: the paying companies with their seats, ARR and next renewal, highest ARR first (the server's order).
   * Non-clients: the companies only. Evidence there opens the why panel.
   */
  openSilent(): void {
    if (!this.params || !this.ranking?.silentCount) {
      return;
    }
    const params = this.params;
    const paying = this.perSeat;
    const period = adbPeriodLong(params.period);
    this.drill.open({
      title: paying ? `Paying companies with no logins in the last ${period}` : `Companies with no logins in the last ${period}`,
      columns: paying ? [{header: 'Seats', align: 'end'}, {header: 'ARR', align: 'end'}, {header: 'Renews', align: 'end'}] : [],
      load: () => this.dataService.getWithExtra<AdminDashboardSilentCompaniesDrill>('AdminDashboardSilentCompaniesDrillQuery', params, {}).pipe(
        map((response): AdminDashboardDrillView => ({
          subtitle: paying
            ? 'Licensed seats nobody is using. Sorted by ARR; each one is a renewal conversation waiting to happen.'
            : 'Companies not in Client Manager that nobody logged in to in the period.',
          rows: (response?.Rows ?? []).map(row => adbDrillRowView(row, paying ? this.silentCells(row) : [])),
          total: response?.Total ?? 0
        }))
      )
    }).subscribe(result => {
      if (result?.action === 'evidence') {
        this.companySelected.emit(result.companyId);
      }
    });
  }

  /** Seats ("–" when unknown), ARR in full USD ("Unknown" without a Client Manager figure), the next renewal "14 Nov" ("–" when none). */
  silentCells(row: AdminDashboardSilentCompanyDrillRow): AdminDashboardDrillCell[] {
    return [
      {text: row.seats === null || row.seats === undefined ? '–' : formatNumber(row.seats, 'en-US', '1.0-0')},
      {text: row.arr === null || row.arr === undefined ? 'Unknown' : adbUsdFull(row.arr)},
      {text: row.nextRenewal ? formatDate(row.nextRenewal, 'd MMM', 'en-US', 'UTC') : '–'}
    ];
  }

  private load(): void {
    this.loadSubscription?.unsubscribe();
    if (!this.params) {
      return;
    }
    // A reload keeps showing the last result until the new one arrives, so only the first load reports 'loading'.
    if (!this.loaded) {
      this.report('loading');
    }
    this.loadSubscription = this.dataService.getData<AdminDashboardActivityRanking>(
      'AdminDashboardActivityRankingQuery', this.params, 'Ranking'
    ).subscribe({
      next: ranking => this.show(ranking ?? null, false),
      error: () => this.show(null, true)
    });
  }

  private show(ranking: AdminDashboardActivityRanking | null, failed: boolean): void {
    this.ranking = ranking;
    this.failed = failed;
    this.loaded = true;
    // The companies that did not log in at all are worth showing even when none is ranked.
    const hasContent = !!ranking?.most?.length || !!ranking?.silentCount;
    this.report(failed ? 'failed' : hasContent ? 'content' : 'empty');
  }

  private report(state: AdminDashboardWidgetState): void {
    this.widgetState = state;
    this.stateChange.emit(state);
  }
}
